import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Users, Calendar, Star, TrendingDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { IMAGES, packages, timetable, faq, results, testimonials, beforeAfters, moreTransformations, CLASS_CAPACITY } from '../mock/mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import BookingDialog from '../components/BookingDialog';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import { fetchBookingCounts } from '../services/api';

function PageHero({ tag, title, sub, image }) {
  return (
    <section className="relative overflow-hidden bg-black">
      <div className="absolute inset-0">
        <img src={image} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
        <div className="text-[11px] tracking-[0.3em] uppercase text-brand">{tag}</div>
        <h1 className="mt-4 font-display italic text-white uppercase text-6xl md:text-8xl leading-[0.9]">{title}</h1>
        {sub && <p className="mt-5 max-w-2xl text-white/70">{sub}</p>}
      </div>
    </section>
  );
}

export default function Transform() {
  const [counts, setCounts] = useState({});
  const [openBook, setOpenBook] = useState(false);
  const [selected, setSelected] = useState(null);

  const refresh = () => fetchBookingCounts().then(setCounts).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const openBooking = (slot, day) => {
    setSelected({ ...slot, day });
    setOpenBook(true);
  };

  return (
    <main>
      <PageHero tag="Invest In You" title={<>Transform<br /><span className="text-brand">Your Fitness</span></>} sub="Personal training, group fitness and online coaching. Pick the format that fits your week, we'll tailor everything else around you." image={IMAGES.groupLycan} />

      <section className="py-20 bg-[#0a0a0d]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <div className="text-[11px] tracking-[0.3em] uppercase text-brand">Packages</div>
            <h2 className="font-display italic text-white text-5xl md:text-6xl uppercase mt-3">Pick Your Plan</h2>
            <p className="text-white/60 mt-4">Below are just examples of the various fitness programmes available. For a tailored plan, please get in touch.</p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {packages.map((p, i) => (
              <Card key={i} className={`reveal lift bg-[#111116] rounded-none p-7 border ${p.highlight ? 'border-brand glow-brand' : 'border-white/5'}`}>
                <div className={`text-[10px] tracking-[0.25em] uppercase ${p.highlight ? 'text-brand' : 'text-white/50'}`}>{p.tag}</div>
                <div className="mt-3 font-display text-3xl uppercase text-white">{p.title}</div>
                <div className="mt-5 flex items-end gap-1">
                  <div className="font-display text-6xl text-white leading-none">{p.price}</div>
                  <div className="text-sm text-white/50 pb-2">{p.unit}</div>
                </div>
                <ul className="mt-6 space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-white/80"><Check className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" /> {f}</li>
                  ))}
                </ul>
                <Link to="/contact">
                  <Button className={`mt-7 w-full rounded-none uppercase tracking-widest text-xs py-5 ${p.highlight ? 'brand-gradient text-white' : 'bg-white text-black hover:bg-brand hover:text-white'}`}>Get Started</Button>
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0d0d10]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <div className="text-[11px] tracking-[0.3em] uppercase text-brand">Weekly Timetable</div>
            <h2 className="font-display italic text-white text-5xl md:text-6xl uppercase mt-3">Book a Class</h2>
            <p className="text-white/60 mt-4">Tap any slot to reserve your spot. Capacity is {CLASS_CAPACITY} per class, once it's full, it's full.</p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {timetable.map((d) => (
              <Card key={d.day} className="reveal bg-[#111116] border-white/5 rounded-none p-6">
                <div className="flex items-center justify-between">
                  <div className="font-display text-2xl uppercase text-white">{d.day}</div>
                  <Badge className="bg-brand text-white border-0 rounded-none text-[10px] tracking-widest uppercase">{d.slots.length} classes</Badge>
                </div>
                <div className="mt-4 divide-y divide-white/5">
                  {d.slots.map((s, i) => {
                    const booked = counts[s.slotId] || 0;
                    const remaining = CLASS_CAPACITY - booked;
                    const full = remaining <= 0;
                    return (
                      <button
                        key={i}
                        onClick={() => !full && openBooking(s, d.day)}
                        disabled={full}
                        className={`w-full py-3 flex items-center justify-between text-left group ${full ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/[0.03]'}`}
                      >
                        <div>
                          <div className="text-white font-semibold group-hover:text-brand transition-colors">{s.name}</div>
                          <div className="text-xs text-white/50 uppercase tracking-wider flex items-center gap-2 mt-0.5">
                            {s.level}
                            <span className="w-1 h-1 rounded-full bg-white/30" />
                            <span className={`${remaining <= 3 && !full ? 'text-brand' : ''}`}>
                              <Users className="w-3 h-3 inline mr-1" />
                              {full ? 'Full' : `${remaining} left`}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="font-display text-xl text-brand">{s.time}</div>
                          {!full && <Calendar className="w-4 h-4 text-white/40 group-hover:text-brand transition-colors" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0a0a0d] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <div className="text-[11px] tracking-[0.3em] uppercase text-brand">Real People, Real Results</div>
            <h2 className="font-display italic text-white text-5xl md:text-6xl uppercase mt-3">Client Transformations</h2>
            <p className="text-white/60 mt-4">Consistent training, honest nutrition and real accountability, here's what happens when it all comes together.</p>
          </div>

          <div className="mt-14 columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-5 [column-fill:_balance]">
            {moreTransformations.map((src, i) => (
              <figure key={i} className="reveal break-inside-avoid mb-4 md:mb-5 relative group overflow-hidden border border-white/5 bg-black">
                <img src={src} alt={`Client transformation ${i + 1}`} loading="lazy" className="w-full h-auto block group-hover:scale-[1.03] transition-transform duration-700" />
                <div className="absolute top-3 left-3 text-[10px] tracking-[0.3em] uppercase text-white bg-brand px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Before / After
                </div>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/60 to-transparent" />
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0a0a0d] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <div className="text-[11px] tracking-[0.3em] uppercase text-brand">In Their Words</div>
            <h2 className="font-display italic text-white text-5xl md:text-6xl uppercase mt-3">Testimonials</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <Card key={i} className="reveal lift bg-[#111116] border-white/5 rounded-none p-8">
                <div className="flex items-center gap-1 text-brand">{[...Array(t.rating)].map((_, r) => <Star key={r} className="w-4 h-4 fill-brand" />)}</div>
                <p className="mt-4 text-white/85 leading-relaxed text-[15px]">"{t.text}"</p>
                <div className="mt-6 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full brand-gradient grid place-items-center text-white font-bold">{t.name.charAt(0)}</div>
                  <div>
                    <div className="font-semibold text-white">{t.name}</div>
                    <div className="text-xs text-white/50 uppercase tracking-wider">{t.role}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-black border-t border-white/5">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <div className="reveal text-center">
            <div className="text-[11px] tracking-[0.3em] uppercase text-brand">Questions?</div>
            <h2 className="font-display italic text-white text-5xl md:text-6xl uppercase mt-3">FAQ</h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faq.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                <AccordionTrigger className="text-white text-left hover:text-brand hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-white/70 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 text-center reveal">
            <Link to="/contact"><Button className="brand-gradient text-white uppercase tracking-widest text-xs rounded-none px-7 py-6 glow-brand">Get a Tailored Quote <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        </div>
      </section>

      <BookingDialog open={openBook} onOpenChange={setOpenBook} slot={selected} onBooked={refresh} />
    </main>
  );
}
